import { useCallback, useContext, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-resource-router';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Flex } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { useJiraCloudId } from 'src/components/create-jira-work-item-modal';
import {
  messages,
  TestCaseAttribute,
  TestStatusEnum,
  ExecutionStrategy,
} from 'src/components/pipelines/components/test-management';
import { DropdownMenuMockContext } from 'src/components/pipelines/contexts/DropdownMenuMockContext';
import { UpdateTestCaseStatusParams } from 'src/components/pipelines/hooks/use-test-management-actions';
import { getRepository } from 'src/components/pipelines/redux/selectors/pipelines';
import { useAnalytics } from 'src/components/settings/analytics';

import { useFixFlakyTest } from '../hooks/use-fix-flaky-test';
import { CreateJiraWorkItemFromTestModal } from '../modals/CreateJiraWorkItemFromTestModal';
import { RemoveTestCaseModal } from '../modals/RemoveTestCaseModal';

type TestCaseInlineActionsProps = {
  testName: string;
  testUuid: string;
  attributes: TestCaseAttribute[];
  executionStrategy: ExecutionStrategy;
  isFixFlakyAvailable: boolean;
  isTestCaseUpdating: boolean;
  isCreateJiraWorkItemEnabled: boolean;
  isFixFlakyActionEnabled: boolean;
  isRemoveActionEnabled: boolean;
  updateTestCaseStatus: (params: UpdateTestCaseStatusParams) => void;
  reloadTestCases: () => void;
};

export const TestCaseInlineActions = ({
  testName,
  testUuid,
  attributes,
  executionStrategy,
  isFixFlakyAvailable,
  isTestCaseUpdating,
  isFixFlakyActionEnabled,
  isRemoveActionEnabled,
  isCreateJiraWorkItemEnabled,
  updateTestCaseStatus,
  reloadTestCases,
}: TestCaseInlineActionsProps) => {
  const { defaultOpen } = useContext(DropdownMenuMockContext);
  const { formatMessage } = useIntl();
  const { publishButtonClickEvent } = useAnalytics({
    source: 'testCaseInlineActions',
  });
  const repository = useSelector(getRepository);

  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isCreateJiraWorkItemModalOpen, setIsCreateJiraWorkItemModalOpen] =
    useState(false);

  const isFlaky = attributes.includes(TestCaseAttribute.FLAKY);
  const isQuarantined = executionStrategy === ExecutionStrategy.QUARANTINED;

  const { cloudId, projectId, hasSites } = useJiraCloudId();
  const showCreateJiraWorkItemAction =
    isCreateJiraWorkItemEnabled && hasSites && !!cloudId;

  const { fixFlakyTest, isFixingFlakyTest } = useFixFlakyTest();

  const toggle = useCallback(
    (name: TestStatusEnum) => {
      if (name === TestStatusEnum.quarantined) {
        updateTestCaseStatus({
          testUuid,
          executionStrategy: isQuarantined
            ? ExecutionStrategy.DEFAULT
            : ExecutionStrategy.QUARANTINED,
        });
        publishButtonClickEvent('toggleQuarantinedStatus', {
          action: isQuarantined ? 'unmarkedQuarantined' : 'markedQuarantined',
        });
      }
    },
    [isQuarantined, testUuid, updateTestCaseStatus, publishButtonClickEvent]
  );

  const showFixFlakyButton = useMemo(() => {
    return (
      isFixFlakyActionEnabled &&
      isFixFlakyAvailable &&
      isFlaky &&
      repository.canWrite
    );
  }, [
    isFixFlakyActionEnabled,
    isFixFlakyAvailable,
    isFlaky,
    repository.canWrite,
  ]);

  const onRemoveClick = useCallback(() => setIsRemoveModalOpen(true), []);
  const onRemoveModalClose = useCallback(() => setIsRemoveModalOpen(false), []);
  const onCreateJiraWorkItemClick = useCallback(() => {
    publishButtonClickEvent('createJiraWorkItemFromTest');
    setIsCreateJiraWorkItemModalOpen(true);
  }, [publishButtonClickEvent]);
  const onCreateJiraWorkItemModalClose = useCallback(
    () => setIsCreateJiraWorkItemModalOpen(false),
    []
  );

  return (
    <>
      <DropdownMenu<HTMLButtonElement>
        placement="bottom-end"
        defaultOpen={defaultOpen}
        trigger={({ triggerRef, ...props }) => (
          <IconButton
            {...props}
            icon={ShowMoreHorizontalIcon}
            label={formatMessage(messages.actions)}
            appearance="subtle"
            ref={triggerRef}
            testId="test-case-inline-action-button"
          />
        )}
        shouldRenderToParent
      >
        <DropdownItemGroup>
          {showFixFlakyButton && (
            <DropdownItem
              isDisabled={isFixingFlakyTest}
              onClick={() => {
                publishButtonClickEvent('FixFlakyTest');
                fixFlakyTest(testUuid);
              }}
            >
              <Flex gap="space.150" alignItems="center">
                {formatMessage(messages.fixFlakyTest)}
              </Flex>
            </DropdownItem>
          )}
          <DropdownItem
            onClick={() => toggle(TestStatusEnum.quarantined)}
            isDisabled={isTestCaseUpdating}
          >
            <Flex gap="space.150" alignItems="center">
              {formatMessage(
                isQuarantined
                  ? messages.unmarkAsQuarantined
                  : messages.markAsQuarantined
              )}
            </Flex>
          </DropdownItem>
          {isRemoveActionEnabled && (
            <DropdownItem
              onClick={onRemoveClick}
              testId="test-case-remove-action"
            >
              <Flex gap="space.150" alignItems="center">
                {formatMessage(messages.removeTest)}
              </Flex>
            </DropdownItem>
          )}
          {showCreateJiraWorkItemAction && (
            <DropdownItem
              onClick={onCreateJiraWorkItemClick}
              testId="test-case-create-jira-work-item-action"
            >
              <Flex gap="space.150" alignItems="center">
                {formatMessage(messages.createJiraWorkItem)}
              </Flex>
            </DropdownItem>
          )}
        </DropdownItemGroup>
      </DropdownMenu>
      {isRemoveActionEnabled && isRemoveModalOpen && (
        <RemoveTestCaseModal
          isOpen={isRemoveModalOpen}
          testName={testName}
          testUuid={testUuid}
          onClose={onRemoveModalClose}
          onDeleteSuccess={reloadTestCases}
        />
      )}
      {showCreateJiraWorkItemAction && isCreateJiraWorkItemModalOpen && (
        <CreateJiraWorkItemFromTestModal
          isOpen={isCreateJiraWorkItemModalOpen}
          testName={testName}
          testUuid={testUuid}
          cloudId={cloudId}
          projectId={projectId}
          onClose={onCreateJiraWorkItemModalClose}
        />
      )}
    </>
  );
};

export const TestExecutionInlineActions = ({
  pipelineURL,
}: {
  pipelineURL: string;
}) => {
  const { defaultOpen } = useContext(DropdownMenuMockContext);
  const { formatMessage } = useIntl();

  return (
    <DropdownMenu<HTMLButtonElement>
      defaultOpen={defaultOpen}
      trigger={({ triggerRef, ...props }) => (
        <IconButton
          {...props}
          icon={ShowMoreHorizontalIcon}
          label={formatMessage(messages.actions)}
          appearance="subtle"
          ref={triggerRef}
          testId="test-execution-inline-action-button"
        />
      )}
      shouldRenderToParent
    >
      <DropdownItemGroup>
        <DropdownItem>
          <Link
            to={pipelineURL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: 'none',
              color: token('color.text'),
            }}
          >
            {formatMessage(messages.viewLogs)}
          </Link>
        </DropdownItem>
      </DropdownItemGroup>
    </DropdownMenu>
  );
};

type TestExecutionMoreMenuProps = {
  testUuid: string;
  testName: string;
  isQuarantined: boolean;
  isTestCaseUpdating: boolean;
  isRemoveActionEnabled: boolean;
  updateTestCaseStatus: (params: UpdateTestCaseStatusParams) => void;
  onDeleteSuccess?: () => void;
};

export const TestExecutionMoreMenu = ({
  testUuid,
  testName,
  isQuarantined,
  isTestCaseUpdating,
  isRemoveActionEnabled,
  updateTestCaseStatus,
  onDeleteSuccess,
}: TestExecutionMoreMenuProps) => {
  const { defaultOpen } = useContext(DropdownMenuMockContext);
  const { formatMessage } = useIntl();
  const { publishButtonClickEvent } = useAnalytics({
    source: 'testExecutionMoreMenu',
  });

  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  const handleQuarantineToggle = useCallback(() => {
    updateTestCaseStatus({
      testUuid,
      executionStrategy: isQuarantined
        ? ExecutionStrategy.DEFAULT
        : ExecutionStrategy.QUARANTINED,
    });
    publishButtonClickEvent('toggleQuarantinedStatus', {
      action: isQuarantined ? 'unmarkedQuarantined' : 'markedQuarantined',
    });
  }, [isQuarantined, testUuid, updateTestCaseStatus, publishButtonClickEvent]);

  const onRemoveClick = useCallback(() => setIsRemoveModalOpen(true), []);
  const onRemoveModalClose = useCallback(() => setIsRemoveModalOpen(false), []);

  return (
    <>
      <DropdownMenu<HTMLButtonElement>
        placement="bottom-end"
        defaultOpen={defaultOpen}
        trigger={({ triggerRef, ...props }) => (
          <IconButton
            {...props}
            icon={ShowMoreHorizontalIcon}
            label={formatMessage(messages.actions)}
            appearance="default"
            ref={triggerRef}
            testId="test-execution-more-menu-button"
          />
        )}
        shouldRenderToParent
      >
        <DropdownItemGroup>
          <DropdownItem
            onClick={handleQuarantineToggle}
            isDisabled={isTestCaseUpdating}
            testId="test-execution-toggle-quarantine-action"
          >
            <Flex gap="space.150" alignItems="center">
              {formatMessage(
                isQuarantined
                  ? messages.unmarkAsQuarantined
                  : messages.markAsQuarantined
              )}
            </Flex>
          </DropdownItem>
          {isRemoveActionEnabled && (
            <DropdownItem
              onClick={onRemoveClick}
              testId="test-execution-remove-action"
            >
              <Flex gap="space.150" alignItems="center">
                {formatMessage(messages.removeTest)}
              </Flex>
            </DropdownItem>
          )}
        </DropdownItemGroup>
      </DropdownMenu>
      {isRemoveActionEnabled && isRemoveModalOpen && (
        <RemoveTestCaseModal
          isOpen={isRemoveModalOpen}
          testName={testName}
          testUuid={testUuid}
          onClose={onRemoveModalClose}
          onDeleteSuccess={onDeleteSuccess}
        />
      )}
    </>
  );
};
